import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Flex,
  Icon,
} from '@kognitos/lattice';
import { TriggerStatusBadge } from '../TriggerTypeSelector/TriggerStatusBadge';
import {
  useRetrieveAllWorkspaceBookConnections,
  useRetrieveWorkspaceBooks,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import { generateMailboxAddress } from '../../utils/email-utils';
import { hasObjectChanges } from '@/shared/utils/comparison-utils';
import { DialogStepper } from '../WebhookTriggerDialog/DialogStepper';
import { MapInputsStep } from '../WebhookTriggerDialog/MapInputsStep';
import { extractTriggerFieldGroups } from '../WebhookTriggerDialog/utils';
import { EmailConfigureStep } from './EmailConfigureStep';
import {
  EMAIL_BOOK_NAME,
  EMAIL_CONNECTION_ID,
  EMAIL_TRIGGER_ID,
  DEFAULT_FILTERS,
  DEFAULT_TRIGGER_FIELD_GROUPS,
} from './constants';
import type { IEmailTriggerDialogProps, IEmailTriggerSaveData } from './types';
import type { IStepConfig } from '../WebhookTriggerDialog/types';
import { TRIGGER_STATUS } from '../TriggerTypeSelector/TriggerType';

/**
 * EmailTriggerDialog Component
 *
 * A multi-step dialog for configuring email triggers.
 * Supports both 'create' mode for new triggers and 'edit' mode for existing triggers.
 *
 * Contains two steps:
 * 1. Configure - View mailbox address and set email filters
 * 2. Map Inputs - Map email payload to automation inputs
 *
 * In edit mode:
 * - Pre-fills form with existing trigger data
 * - Shows dynamic button labels based on whether changes were made
 * - Displays "Pause Trigger" button in footer
 *
 * @param props - Component props
 * @returns JSX element containing the dialog
 */
export function EmailTriggerDialog({
  isOpen,
  onOpenChange,
  inputSpecs,
  automationId,
  automationName,
  isDraft = true,
  isLoading = false,
  onSave,
  mode = 'create',
  initialData,
  triggerName,
  triggerStatus,
  onToggleActiveState,
  isToggleActiveStateLoading,
  onDelete,
  onUpdate,
  currentStep,
  onStepChange,
  filters,
  onFiltersChange,
  inputMappings,
  onInputMappingsChange,
}: IEmailTriggerDialogProps) {
  const { t } = useTranslation('automation-inputs');

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Check if inputSpecs has any items
  const hasInputSpecs = useMemo(
    () => inputSpecs && inputSpecs.length > 0,
    [inputSpecs],
  );

  // Generate mailbox address for this automation
  const mailboxAddress = useMemo(
    () =>
      initialData?.mailboxAddress ??
      generateMailboxAddress(automationId, isDraft, automationName),
    [automationId, initialData?.mailboxAddress, isDraft, automationName],
  );

  // Fetch the email book connection to determine the correct book version
  const { data: emailConnectionsData } = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    {
      page_size: 600,
      filter: FilterBuilder.create()
        .equals('book_name', EMAIL_BOOK_NAME)
        .build(),
    },
    {
      query: {
        enabled: !!organizationId && !!workspaceId,
      },
    },
  );

  const emailBookVersion = emailConnectionsData?.data?.book_connections?.find(
    c => c.connection_id === EMAIL_CONNECTION_ID,
  )?.book_version;

  // Fetch the email book matching the connection's version
  const { data: emailBookData } = useRetrieveWorkspaceBooks(
    organizationId,
    workspaceId,
    {
      page_size: 1,
      filter: FilterBuilder.create()
        .equals('name', EMAIL_BOOK_NAME)
        .equals('version', emailBookVersion ?? '')
        .build(),
    },
    {
      query: {
        enabled: !!organizationId && !!workspaceId && !!emailBookVersion,
      },
    },
  );

  // Extract trigger field groups from the email book's new_email trigger
  const triggerFieldGroupsFromApi = useMemo(() => {
    const emailBook = emailBookData?.data?.books?.[0];
    if (!emailBook?.triggers) return DEFAULT_TRIGGER_FIELD_GROUPS;

    const newEmailTrigger = emailBook.triggers.find(
      trigger => trigger.id === EMAIL_TRIGGER_ID,
    );
    if (!newEmailTrigger) return DEFAULT_TRIGGER_FIELD_GROUPS;

    return extractTriggerFieldGroups(newEmailTrigger);
  }, [emailBookData?.data?.books]);

  // Use API-fetched trigger field groups, fallback to initialData or defaults
  const triggerFieldGroups = useMemo(() => {
    // Prefer initialData for edit mode if available
    if (initialData?.triggerFieldGroups?.length) {
      return initialData.triggerFieldGroups;
    }
    // Otherwise use API-fetched data
    return triggerFieldGroupsFromApi;
  }, [initialData?.triggerFieldGroups, triggerFieldGroupsFromApi]);

  /**
   * Checks if filter values have changed from initial values
   * Treats empty values (null, undefined, '', []) as equivalent
   */
  const hasFilterChanges = useMemo(() => {
    if (mode === 'create') return false;
    const initialFilters = initialData?.filters ?? DEFAULT_FILTERS;
    return hasObjectChanges(filters, initialFilters);
  }, [mode, filters, initialData]);

  /**
   * Checks if mapping values have changed from initial values
   * Treats empty values (null, undefined, '') as equivalent
   */
  const hasMappingChanges = useMemo(() => {
    if (mode === 'create') return false;
    const initialMappings = initialData?.inputMappings ?? {};
    return hasObjectChanges(inputMappings, initialMappings);
  }, [mode, inputMappings, initialData]);

  /**
   * Checks if any changes have been made across all steps
   */
  const hasAnyChanges = useMemo(() => {
    return hasFilterChanges || hasMappingChanges;
  }, [hasFilterChanges, hasMappingChanges]);

  /**
   * Handles mapping change for an input
   */
  const handleMappingChange = useCallback(
    (inputName: string, triggerFieldValue: string) => {
      onInputMappingsChange({
        ...inputMappings,
        [inputName]: triggerFieldValue,
      });
    },
    [inputMappings, onInputMappingsChange],
  );

  // Step configuration - only show step 2 if there are inputSpecs
  const steps: IStepConfig[] = useMemo(() => {
    const baseSteps: IStepConfig[] = [
      {
        number: 1,
        title: t('emailDialog.steps.configure.title'),
        description: t('emailDialog.steps.configure.description'),
      },
    ];

    if (hasInputSpecs) {
      baseSteps.push({
        number: 2,
        title: t('emailDialog.steps.mapInputs.title'),
        description: t('emailDialog.steps.mapInputs.description'),
      });
    }

    return baseSteps;
  }, [hasInputSpecs, t]);

  /**
   * Handles navigation to the previous step
   */
  const handleBack = () => {
    if (currentStep > 1) {
      onStepChange(currentStep - 1);
    }
  };

  /**
   * Gets the current form data
   */
  const getFormData = useCallback((): IEmailTriggerSaveData => {
    return {
      mailboxAddress,
      filters,
      inputMappings,
      triggerFieldGroups,
    };
  }, [mailboxAddress, filters, inputMappings, triggerFieldGroups]);

  /**
   * Handles navigation to the next step or saves/updates if on the last step
   */
  const handleNext = () => {
    // If no inputSpecs, save directly from step 1
    if (!hasInputSpecs && currentStep === 1) {
      const formData = getFormData();

      if (mode === 'edit') {
        if (hasAnyChanges) {
          onUpdate?.(formData);
        } else {
          // No changes - just close
          onOpenChange(false);
        }
      } else {
        // Create mode
        onSave?.(formData);
      }
      return;
    }

    // Multi-step flow (has inputSpecs)
    if (currentStep < steps.length) {
      onStepChange(currentStep + 1);
    } else {
      // Last step - save or update based on mode
      const formData = getFormData();

      if (mode === 'edit') {
        if (hasAnyChanges) {
          onUpdate?.(formData);
        } else {
          // No changes - just close
          onOpenChange(false);
        }
      } else {
        // Create mode
        onSave?.(formData);
      }
    }
  };

  /**
   * Handles dialog open state changes
   * Prevents closing when loading
   */
  const handleOpenChange = (open: boolean) => {
    if (isLoading) return;
    onOpenChange(open);
  };

  const isLastStep = currentStep === steps.length;
  const isFirstStep = currentStep === 1;
  const isEditMode = mode === 'edit';
  const isReadOnly = mode === 'readOnly';

  /**
   * Renders the current step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <EmailConfigureStep
            mailboxAddress={mailboxAddress}
            filters={filters}
            onFiltersChange={onFiltersChange}
            readOnly={isReadOnly}
          />
        );
      case 2:
        // Only render step 2 if there are inputSpecs
        if (!hasInputSpecs) {
          return null;
        }
        return (
          <MapInputsStep
            inputSpecs={inputSpecs}
            mappings={inputMappings}
            triggerFieldGroups={triggerFieldGroups}
            onMappingChange={handleMappingChange}
            readOnly={isReadOnly}
          />
        );
      default:
        return null;
    }
  };

  /**
   * Gets the dynamic primary button label based on mode, step, and changes
   */
  const getPrimaryButtonLabel = () => {
    if (isLoading) {
      return isEditMode
        ? t('trigger.actions.updating')
        : t('trigger.actions.saving');
    }

    // If no inputSpecs, we're always on step 1 (single step)
    if (!hasInputSpecs) {
      if (isEditMode) {
        return hasAnyChanges
          ? t('trigger.actions.updateTrigger')
          : t('trigger.actions.done');
      }
      // Create mode with no inputSpecs
      return t('trigger.actions.saveTrigger');
    }

    // Multi-step flow (has inputSpecs)
    if (isEditMode) {
      if (isLastStep) {
        return hasAnyChanges
          ? t('trigger.actions.updateTrigger')
          : t('trigger.actions.done');
      }
      // First step in edit mode
      return hasFilterChanges
        ? t('trigger.actions.saveAndNext')
        : t('trigger.actions.next');
    }

    // Create mode
    return isLastStep
      ? t('trigger.actions.saveTrigger')
      : t('trigger.actions.saveAndNext');
  };

  /**
   * Gets the dynamic primary button variant based on mode, step, and changes
   * In edit mode: secondary for "Next" and "Done" (no changes), default for others
   */
  const getPrimaryButtonVariant = (): 'default' | 'secondary' => {
    if (isEditMode) {
      if (isLastStep) {
        return hasAnyChanges ? 'default' : 'secondary';
      }
      return hasFilterChanges ? 'default' : 'secondary';
    }

    return 'default';
  };

  /**
   * Gets the dialog title based on mode
   */
  const getDialogTitle = () => {
    if (isReadOnly && triggerName) {
      return t('emailDialog.viewTitle', { triggerName });
    }
    if (isEditMode && triggerName) {
      return t('emailDialog.editTitle', { triggerName });
    }
    return t('emailDialog.title');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-5 gap-5 sm:max-w-2xl max-h-[calc(100vh-100px)] flex flex-col"
        showCloseButton={!isLoading}
      >
        {/* Header */}
        <DialogHeader className="gap-1">
          <Flex align="center" gap={2}>
            <DialogTitle className="text-xl font-medium">
              {getDialogTitle()}
            </DialogTitle>
            {triggerStatus && <TriggerStatusBadge status={triggerStatus} />}
          </Flex>
          <DialogDescription className="text-sm">
            {hasInputSpecs
              ? t('emailDialog.description')
              : t('emailDialog.noInputsDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper - only show if there are multiple steps */}
        {hasInputSpecs && (
          <DialogStepper steps={steps} currentStep={currentStep} />
        )}

        {/* Step Content */}
        <Flex className="flex-1 overflow-y-auto min-h-0" direction="column">
          {renderStepContent()}
        </Flex>

        {/* Footer */}
        <DialogFooter
          className={
            onToggleActiveState || onDelete
              ? 'sm:justify-between'
              : 'sm:justify-end'
          }
        >
          {(onToggleActiveState || onDelete) && (
            <Flex gap={2}>
              {onToggleActiveState && (
                <Button
                  variant="secondary"
                  onClick={onToggleActiveState}
                  disabled={
                    isToggleActiveStateLoading ||
                    triggerStatus === TRIGGER_STATUS.ERROR ||
                    triggerStatus === TRIGGER_STATUS.PENDING
                  }
                  isLoading={isToggleActiveStateLoading}
                >
                  {triggerStatus === TRIGGER_STATUS.ACTIVE ? (
                    <Icon size="md" type="Pause" />
                  ) : (
                    <Icon size="md" type="Play" />
                  )}
                  {triggerStatus === TRIGGER_STATUS.ACTIVE
                    ? t('trigger.actions.pause')
                    : t('trigger.actions.resume')}
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="destructive"
                  onClick={onDelete}
                  disabled={isToggleActiveStateLoading}
                >
                  <Icon size="md" type="Trash" />
                  {t('trigger.actions.delete')}
                </Button>
              )}
            </Flex>
          )}

          {isReadOnly ? (
            /* Read-only mode - just show Close button */
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isFirstStep}
              >
                {t('trigger.actions.back')}
              </Button>
              {isLastStep ? (
                <Button variant="default" onClick={() => onOpenChange(false)}>
                  {t('trigger.actions.close')}
                </Button>
              ) : (
                <Button variant="default" onClick={handleNext}>
                  {t('trigger.actions.next')}
                </Button>
              )}
            </Flex>
          ) : (
            /* Create/Edit mode - Back and Primary buttons */
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isFirstStep || isLoading}
              >
                {t('trigger.actions.back')}
              </Button>
              <Button
                variant={getPrimaryButtonVariant()}
                onClick={handleNext}
                disabled={isLoading}
                isLoading={isLoading}
              >
                {getPrimaryButtonLabel()}
              </Button>
            </Flex>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
